import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { SERVICE_TICKET_STATUSES_SEED } from '@common/data';
import type {
  ServiceTicketStatusDef,
  ServiceTicketStatusDeleteCheck,
  ServiceTicketStatusDraft,
} from '@common/types';

import { ServiceTicketsService } from './service-tickets.service';

const LIST_LATENCY_MS = 150;
const SAVE_LATENCY_MS = 250;

@Injectable({ providedIn: 'root' })
export class ServiceTicketStatusesService {
  private readonly tickets = inject(ServiceTicketsService);

  private readonly statuses = signal<ServiceTicketStatusDef[]>(
    SERVICE_TICKET_STATUSES_SEED.slice(),
  );
  private readonly _revision = signal<number>(0);
  private seq = 0;

  readonly revision = this._revision.asReadonly();

  readonly all: Signal<readonly ServiceTicketStatusDef[]> = computed(() =>
    this.statuses()
      .slice()
      .sort((a, b) => a.order - b.order),
  );

  readonly enabled: Signal<readonly ServiceTicketStatusDef[]> = computed(() =>
    this.all().filter((s) => s.isEnabled),
  );

  readonly byKey: Signal<ReadonlyMap<string, ServiceTicketStatusDef>> = computed(
    () => new Map(this.all().map((s) => [s.key, s])),
  );

  readonly terminalKeys: Signal<ReadonlySet<string>> = computed(
    () =>
      new Set(
        this.all()
          .filter((s) => s.isTerminal)
          .map((s) => s.key),
      ),
  );

  list(): Observable<readonly ServiceTicketStatusDef[]> {
    return of(this.all().slice()).pipe(delay(LIST_LATENCY_MS));
  }

  getById(id: string): ServiceTicketStatusDef | undefined {
    return this.statuses().find((s) => s.id === id);
  }

  isTerminal(key: string | null | undefined): boolean {
    if (!key) return false;
    return this.terminalKeys().has(key);
  }

  create(draft: ServiceTicketStatusDraft): Observable<ServiceTicketStatusDef> {
    const normalizedLabel = draft.label.trim();
    if (!normalizedLabel) {
      return throwError(() => new Error('Status label is required.'));
    }
    if (this.labelTaken(normalizedLabel, null)) {
      return throwError(() => new Error(`A status named "${normalizedLabel}" already exists.`));
    }
    const nextOrder = this.statuses().reduce((max, s) => Math.max(max, s.order), 0) + 1;
    const created: ServiceTicketStatusDef = {
      id: this.generateId(normalizedLabel),
      key: this.generateKey(normalizedLabel),
      label: normalizedLabel,
      icon: draft.icon.trim() || 'label',
      tone: draft.tone,
      isTerminal: draft.isTerminal,
      isEnabled: true,
      isDefault: false,
      order: nextOrder,
    };
    this.statuses.update((rows) => [...rows, created]);
    this._revision.update((n) => n + 1);
    return of(created).pipe(delay(SAVE_LATENCY_MS));
  }

  update(id: string, patch: ServiceTicketStatusDraft): Observable<ServiceTicketStatusDef> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Status not found.'));
    const normalizedLabel = patch.label.trim();
    if (!normalizedLabel) {
      return throwError(() => new Error('Status label is required.'));
    }
    if (this.labelTaken(normalizedLabel, id)) {
      return throwError(() => new Error(`A status named "${normalizedLabel}" already exists.`));
    }
    if (
      current.isTerminal !== patch.isTerminal &&
      this.tickets.all().some((t) => t.status === current.key)
    ) {
      return throwError(
        () => new Error('Move existing tickets before changing whether this status closes a case.'),
      );
    }
    const next: ServiceTicketStatusDef = {
      ...current,
      label: normalizedLabel,
      icon: patch.icon.trim() || current.icon,
      tone: patch.tone,
      isTerminal: patch.isTerminal,
    };
    this.statuses.update((rows) => rows.map((s) => (s.id === id ? next : s)));
    this._revision.update((n) => n + 1);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  setEnabled(id: string, enabled: boolean): Observable<ServiceTicketStatusDef> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Status not found.'));
    const next: ServiceTicketStatusDef = { ...current, isEnabled: enabled };
    this.statuses.update((rows) => rows.map((s) => (s.id === id ? next : s)));
    this._revision.update((n) => n + 1);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  deleteCheck(id: string): ServiceTicketStatusDeleteCheck {
    const current = this.getById(id);
    if (!current) return { canDelete: false, ticketCount: 0 };
    const count = this.tickets.all().filter((t) => t.status === current.key).length;
    return { canDelete: count === 0, ticketCount: count };
  }

  delete(id: string): Observable<void> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Status not found.'));
    if (current.isDefault) {
      return throwError(() => new Error('Default statuses cannot be deleted — disable instead.'));
    }
    const check = this.deleteCheck(id);
    if (!check.canDelete) {
      return throwError(
        () => new Error(`Cannot delete: ${check.ticketCount} ticket(s) still use this status.`),
      );
    }
    this.statuses.update((rows) => rows.filter((s) => s.id !== id));
    this._revision.update((n) => n + 1);
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  reorder(orderedIds: readonly string[]): Observable<readonly ServiceTicketStatusDef[]> {
    const positions = new Map(orderedIds.map((id, index) => [id, index + 1]));
    this.statuses.update((rows) =>
      rows.map((s) => {
        const nextOrder = positions.get(s.id);
        return nextOrder === undefined ? s : { ...s, order: nextOrder };
      }),
    );
    this._revision.update((n) => n + 1);
    return of(this.all().slice()).pipe(delay(SAVE_LATENCY_MS));
  }

  ticketCounts(): ReadonlyMap<string, number> {
    const counts = new Map<string, number>();
    for (const t of this.tickets.all()) {
      counts.set(t.status, (counts.get(t.status) ?? 0) + 1);
    }
    return counts;
  }

  private labelTaken(label: string, excludeId: string | null): boolean {
    const lowered = label.toLowerCase();
    return this.statuses().some((s) => s.id !== excludeId && s.label.toLowerCase() === lowered);
  }

  private generateId(label: string): string {
    this.seq += 1;
    return `sts-custom-${this.slug(label)}-${Date.now().toString(36)}-${this.seq}`;
  }

  private generateKey(label: string): string {
    const base = this.slug(label) || 'status';
    const existing = new Set(this.statuses().map((s) => s.key));
    if (!existing.has(base)) return base;
    let i = 2;
    while (existing.has(`${base}-${i}`)) i += 1;
    return `${base}-${i}`;
  }

  private slug(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);
  }
}
